import type { ReactNode } from 'react'
import { cx } from './cx'

/**
 * The white-chip-on-grey toggle used for range pickers and filter chips.
 * Extracted from the eleven hand-written copies across Business, Infrastructure,
 * Analytics, Users and Instance Detail.
 *
 * Buttons carry `aria-pressed` rather than radio semantics: radios would imply
 * arrow-key navigation, and adding that here would be new behaviour, not an
 * extraction. The interaction is exactly what it was.
 */
export interface SegmentedOption<V extends string> {
  value: V
  label: ReactNode
}

const SIZE = {
  sm: 'px-3 py-1 text-xs',
  md: 'px-3 py-1.5 text-sm',
} as const

export default function SegmentedControl<V extends string>({
  value,
  onChange,
  options,
  size = 'md',
  label,
  className,
}: {
  value: V
  onChange: (value: V) => void
  options: ReadonlyArray<SegmentedOption<V>>
  size?: keyof typeof SIZE
  /** Names the group for screen readers, e.g. "Date range". */
  label?: string
  className?: string
}) {
  return (
    <div className={cx('flex gap-1 rounded-lg bg-gray-100 p-1', className)} role="group" aria-label={label}>
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={cx(
              'rounded-md font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              SIZE[size],
              active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
